import { Alert, Avatar, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { LinkOff as RevocadoIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import BotonVolver from "../../components/kit/BotonVolver";
import Correo from "../../components/kit/Correo";
import EnlaceTema from "../../components/tema/EnlaceTema";
import { useEstudiante } from "../../queries/useEstudiantes";
import { useAuthStore } from "../../stores/authStore";
import ExpedienteGuardado from "../../components/app/ExpedienteGuardado";
import ProcesosPorSemestre from "../../components/app/ProcesosPorSemestre";
import { RevocacionTema } from "../../types/estudiante";
import { tokens } from "../../theme";
import { formatFechaHora } from "../../utils/fecha";
import { etiquetaPeriodo } from "../../utils/semestre";

/**
 * Ficha del estudiante (para guía/evaluador/coordinador): sus datos y todos sus
 * procesos con los documentos subidos. El backend gatea el acceso.
 */
export default function EstudianteDetalle() {
  const { rut } = useParams();
  const { user } = useAuthStore();
  const { data, isLoading, isError } = useEstudiante(Number(rut));
  const esCoordinador = !!user?.es_coordinador_global;

  return (
    <Box>
      <BotonVolver fallback="/app/estudiantes" etiquetaFallback="Estudiantes" />

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudo cargar la ficha (¿tienes permiso para ver este estudiante?).</Alert>}

      {data && (
        <>
          <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Avatar src={data.foto ?? undefined} sx={{ width: 64, height: 64, flexShrink: 0 }}>
                {data.nombre.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="h4">{data.nombre}</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1.25, color: "text.secondary" }}>
                  <Typography variant="body2">RUT {data.rut}</Typography>
                  {data.email && <Correo correo={data.email} />}
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* Entre el estudiante y sus procesos: el otro expediente, el administrativo,
              que vive en UCampus. Plegado, y solo para quien puede consultarlo. */}
          {esCoordinador && <ExpedienteGuardado rut={Number(rut)} />}

          <Revocaciones revocaciones={data.revocaciones} />

          {data.instancias.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography color="text.secondary">Este estudiante no tiene procesos registrados.</Typography>
            </Paper>
          ) : (
            <ProcesosPorSemestre instancias={data.instancias} showEstudiante={false} gestionCoordinador={esCoordinador} />
          )}

        </>
      )}
    </Box>
  );
}

/**
 * Los temas que le quitaron.
 *
 * Va arriba de los procesos y no escondido al final: al revocar, el proceso queda sin
 * tema y sin guía —igual que el de alguien que nunca tomó uno—, así que sin esto el
 * hueco en su historial no se explica solo.
 */
function Revocaciones({ revocaciones }: { revocaciones: RevocacionTema[] }) {
  if (!revocaciones.length) return null;
  return (
    <Stack spacing={1.5} sx={{ mb: 3 }}>
      {revocaciones.map((r) => (
        <Alert key={r.id} severity="warning" icon={<RevocadoIcon fontSize="small" />}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.tabla.celdaFuerte }}>
            Se le quitó el tema <EnlaceTema id={r.tema_id} titulo={r.tema} />
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            {r.motivo}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {formatFechaHora(r.fecha)}
            {r.paso && ` · estaba en ${r.paso}`}
            {r.periodo && ` · ${etiquetaPeriodo(r.periodo)}`}
            {r.por && ` · ${r.por}`}
          </Typography>
        </Alert>
      ))}
    </Stack>
  );
}
